import { Col, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './EcomStats.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircle,
  faPause,
  faSquare,
  faStar,
  faXmark,
  faArrowRight,
  faCube,
  faExclamationTriangle,
  faTimesCircle,
  faCreditCard,
  faCheckCircle,
  faSpinner,
  faShoppingCart,
  faExchangeAlt,
  faTruck
} from '@fortawesome/free-solid-svg-icons';

type StatType = {
  id: number | string;
  icon: IconProp;
  title: string;
  subTitle: string;
  color: string;
  onClick?: () => void;
};

type EnhancedEcomStatsProps = {
  // Order stats
  newOrders?: number;
  completeOrders?: number; // Changed from pendingPaymentOrders
  readyToReturnOrders?: number; // Changed from paidOrders
  
  // Product stats
  totalProducts?: number;
  lowStockProducts?: number;
  closedOrDisapprovedProducts?: number;
  
  loading?: boolean;
  
  // Click handlers for orders
  onNewOrdersClick?: () => void;
  onPendingPaymentOrdersClick?: () => void; // Keeping old name for backward compatibility
  onPaidOrdersClick?: () => void; // Keeping old name for backward compatibility
  
  // Click handlers for products
  onTotalProductsClick?: () => void;
  onLowStockProductsClick?: () => void;
  onClosedOrDisapprovedProductsClick?: () => void;
};

const EnhancedEcomStats = ({ 
  // Order stats
  newOrders = 0,
  completeOrders = 0, // Changed from pendingPaymentOrders
  readyToReturnOrders = 0, // Changed from paidOrders
  
  // Product stats
  totalProducts = 0,
  lowStockProducts = 0,
  closedOrDisapprovedProducts = 0,
  
  loading = false,
  
  // Click handlers for orders
  onNewOrdersClick,
  onPendingPaymentOrdersClick,
  onPaidOrdersClick,
  
  // Click handlers for products
  onTotalProductsClick,
  onLowStockProductsClick,
  onClosedOrDisapprovedProductsClick
}: EnhancedEcomStatsProps) => {
  
  const { t } = useTranslation();
  
  // Order stats - with complete orders first and new orders second
  const orderStats: StatType[] = [
    {
      id: 2,
      icon: faCheckCircle,
      title: t('orderStats.completeOrders', { count: completeOrders }),
      subTitle: t('orderStats.completeOrdersLabel'),
      color: 'success', // Green color for completed orders
      onClick: onPendingPaymentOrdersClick
    },
    {
      id: 1,
      icon: faShoppingCart,
      title: t('orderStats.newOrders', { count: newOrders }),
      subTitle: t('orderStats.awaitingProcessing'),
      color: 'primary', // Blue color for new/processing orders
      onClick: onNewOrdersClick
    },
    {
      id: 3,
      icon: faExchangeAlt, 
      title: t('orderStats.readyToReturnOrders', { count: readyToReturnOrders }),
      subTitle: t('orderStats.readyToReturnOrdersLabel'),
      color: 'warning', // Orange color for return-related status
      onClick: onPaidOrdersClick
    }
  ];
  
  // Product stats
  const productStats: StatType[] = [
    {
      id: 4,
      icon: faCube,
      title: t('productStats.totalProducts', { count: totalProducts }),
      subTitle: t('productStats.totalProductsLabel'),
      color: 'primary',
      onClick: onTotalProductsClick
    },
    {
      id: 5,
      icon: faExclamationTriangle,
      title: t('productStats.lowStock', { count: lowStockProducts }),
      subTitle: t('productStats.lowStockLabel'),
      color: 'warning',
      onClick: onLowStockProductsClick
    },
    {
      id: 6,
      icon: faTimesCircle,
      title: t('productStats.closedProducts', { count: closedOrDisapprovedProducts }),
      subTitle: t('productStats.closedProductsLabel'),
      color: 'danger',
      onClick: onClosedOrDisapprovedProductsClick
    }
  ];
  
  return (
    <div className="stats-container">
      {/* Order Stats */}
      <div className="mb-4">
        {/* <h6 className="mb-3 text-secondary fw-semibold">Order Statistics</h6> */}
        <Row className="align-items-center g-4">
          {orderStats.map(stat => (
            <Col xs={12} md="auto" key={stat.id}>
              <Stat stat={stat} />
            </Col>
          ))}
        </Row>
      </div>
      
      {/* Divider */}
      <div className="my-4 border-bottom"></div>
      
      {/* Product Stats */}
      <div>
        {/* <h6 className="mb-3 text-secondary fw-semibold">Product Statistics</h6> */}
        <Row className="align-items-center g-4">
          {productStats.map(stat => (
            <Col xs={12} md="auto" key={stat.id}>
              <Stat stat={stat} />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

const Stat = ({ stat }: { stat: StatType }) => {
  return (
    <Stack 
      direction="horizontal" 
      className={`align-items-center ${stat.onClick ? 'stat-clickable' : ''}`}
      onClick={stat.onClick} 
      style={{ cursor: stat.onClick ? 'pointer' : 'default' }}
    >
      <span
        className="fa-layers"
        style={{ minHeight: '46px', minWidth: '46px' }}
      >
        <FontAwesomeIcon
          icon={faSquare}
          size="2x"
          className={`text-${stat.color}-light dark__text-opacity-50`}
          transform="down-4 rotate--10 left-4"
        />
        <FontAwesomeIcon
          icon={faCircle}
          size="2x"
          className={`${stat.color === 'primary' ? 'text-primary-lighter' : `text-stats-circle-${stat.color}`} fa-layers-circle`}
          transform="up-4 right-3 grow-2"
        />
        <FontAwesomeIcon
          icon={stat.icon}
          size="1x"
          className={`text-${stat.color}`}
          transform="shrink-2 up-8 right-6"
        />
      </span>

      <div className="ms-3">
        <h4 className="mb-0">{stat.title}</h4>
        <p className="text-body-secondary fs-9 mb-0">{stat.subTitle}</p>
      </div>
    </Stack>
  );
};

export default EnhancedEcomStats;
